// Single source of truth for "where is this tool actually offered" — shared by the Tools tab
// (Enabled tools) and the Modules tab (Screens), so the two views can never classify the same
// agent differently. A tool is only ever "enabled" in the sense SAM's scope filter cares about
// when it is BOTH owned (in `agent.tools`) AND placed somewhere (core, global override, or a
// screen override) — see api-service/app/services/llm/page_tools.py and TOOL_SCOPE.md. Owned-but-
// unplaced tools are real bugs (invisible to the agent despite looking "enabled"); this module is
// what lets both tabs surface that instead of silently disagreeing.

export interface ToolPlacement {
  /** Passes the scope filter unconditionally whenever owned — CORE_TOOLS or admin global_tools. */
  isGlobal: boolean;
  /** Screen modules (slugs) whose own override explicitly lists this tool. */
  screens: string[];
}

export interface ScopeOverrides {
  global_tools: string[];
  screens: Record<string, string[]>;
}

/**
 * Classifies every owned tool name's placement. `ownedNames` should be every name actually
 * attached to the agent (`tools[].name || tools[].tool_name`) — a name placed in `overrides` but
 * not owned is a different bug (placed-but-not-owned) and intentionally left out of the result;
 * callers that need to flag *that* case should diff `overrides` against `ownedNames` themselves.
 */
export function computeToolPlacements(
  ownedNames: Iterable<string>,
  overrides: ScopeOverrides,
  coreTools: string[],
): Map<string, ToolPlacement> {
  const globalSet = new Set([...coreTools, ...overrides.global_tools]);
  const screensByName = new Map<string, string[]>();
  Object.entries(overrides.screens).forEach(([module, names]) => {
    names.forEach((n) => {
      const list = screensByName.get(n);
      if (list) list.push(module); else screensByName.set(n, [module]);
    });
  });

  const result = new Map<string, ToolPlacement>();
  for (const name of ownedNames) {
    result.set(name, {
      isGlobal: globalSet.has(name),
      screens: screensByName.get(name) ?? [],
    });
  }
  return result;
}

/** True when a tool is owned but placed nowhere — attached yet invisible to the agent in scoped mode. */
export function isUnplaced(placement: ToolPlacement | undefined): boolean {
  return !placement || (!placement.isGlobal && placement.screens.length === 0);
}

export function sortToolNames(names: string[]): string[] {
  return [...names].sort((a, b) => a.localeCompare(b));
}
